import { useEffect, useState } from "react";

import * as dateFns from 'date-fns';

interface IProps<T> {
  data: T[] | null;
  defaultSort?: ISort;
}

export interface ISort {
  key: string | null;
  reverse: boolean;
  type: TSortType;
}

export type TSortType = "string" | "int" | "date" | "boolean";

const getValue = <T>(key: string, item: T): string => {
  if (item == null) return "" as any;
  if (key.indexOf(".") !== -1) {
    const split = key.split(".");
    const part = split.shift() as string;
    return getValue(split.join('.'), item[part as keyof T]);
  }

  return item[key as keyof T] as unknown as string;
}

export const useTableSort = <T>({ data, defaultSort }: IProps<T>) => {
  const [sort, setSort] = useState<ISort>(defaultSort || {
    key: null,
    reverse: false,
    type: "string",
  });
  const [items, setItems ] = useState<T[]>([]);

  useEffect(() => {
    if (data) {
      setItems(data);
      setSort(sort => ({...sort}));
    }
  }, [data]);

  useEffect(() => {
    const { key, reverse, type } = sort;
    if(key) {
      setItems(items => {
        const newArray = [...items];

        newArray.sort((a, b) => {
          let aValue: string | number | Date | boolean = getValue(key, a);
          let bValue: string | number | Date | boolean = getValue(key, b);

          const multiplier = reverse ? -1 : 1;

          let aValueStr = aValue != null ? String(aValue).trim() : "";
          let bValueStr = bValue != null ? String(bValue).trim() : "";

          let aValueEmpty = aValueStr.length === 0;
          let bValueEmpty = bValueStr.length === 0;

          if (aValueEmpty && bValueEmpty) return 0;
          if (aValueEmpty) return 1;
          if (bValueEmpty) return -1;

          if (type === "date") {
            const dateA = dateFns.parseISO(aValueStr).getTime();
            const dateB = dateFns.parseISO(bValueStr).getTime();
            if (dateA > dateB) return 1 * multiplier;
            if (dateB > dateA) return -1 * multiplier;
            return 0;
          } else if (type === "int") {
            const intA = parseInt(aValueStr, 10);
            const intB = parseInt(bValueStr, 10);
            if (intA > intB) return 1 * multiplier;
            if (intB > intA) return -1 * multiplier;
            return 0;
          } else {
            return aValueStr.localeCompare(bValueStr, "fi", { numeric: true, sensitivity: "base" }) * multiplier;
          }
        });

        return newArray;
      });
    }

  }, [sort]);


  const onSort = (key: string, type: TSortType = "string") => {
    setSort((prevSort) => {
      let reverse = false;
      if (prevSort.key === key) {
        reverse = !prevSort.reverse;
      }
      return {
        key,
        reverse,
        type,
      };
    });
  }


  return { sort, onSort, items };
}